import { h } from 'preact';
import { defaults } from '../config';
import { fahrenheitToCelsius, replaceFwdSlash, handleSunFormat } from '../lib/helpers/index';
import localTempStore from '../lib/dbee-client';
import isDateToday from '../lib/is-date-today';
import { DEFAULT_FORECAST_DAYS, NOON, Errors } from '../constants';
import * as weatherClient from '../lib/weather-client';

// Hour, in military, for the cutoff of showing "today" in the weather
// Used to remove today's weather and leave only tonight's weather after a
// certain period of time.
const TODAY_CUTTOFF = 18;

/**
 * This contains logic necessary to interact with the weather service, as well as
 * sanitizing the response for use on the front end. It is exported as a class in
 * order to facilitate testing, even though in general each function could be
 * exported by itself as it has no state dependencies.
 *
 */
export class WeatherModel {
	constructor(config = {}) {
		// TODO: Still hitting Prod on UAT
		Object.assign(this, { ...defaults, ...config });
		weatherClient.setApiOrigin(config.weatherServiceOrigin || defaults.weatherServiceOrigin);
	}


	_getLocalTempUnit = localTempStore.get;

	_setLocalTempUnit = localTempStore.set;

	setTemperatureScale = tempUnit => {
		this._setLocalTempUnit(tempUnit);
	};

	getTemperatureScale = () => {
		let { tempUnit } = this._getLocalTempUnit();
		return tempUnit;
	};

	/**
	 * Query the location service
	 *
	 * @param {string} geoId - Unique geoID value
	 * @returns {Promise<Object>}
	 */
	fetchLocationDataFromService = (geoId) => {
		let queryString = this.locationServiceOrigin.replace(/\/$/, '') + `/geonameid/${geoId}/`;
		return fetch(queryString).then(r => r.json());
	};

	/**
	 * Query the location service to find a US Zipcode from it's geoname ID value
	 *
	 * @param {string} geoId - Geo ID value, i.e. "5705957"
	 * @returns {Promise<string|null>}
	 */
	fetchUsZipFromGeo = geoId =>
		this.fetchLocationDataFromService(geoId).then(body => {
			let entry = body.documents[0];

			return entry.geoname_id;
		});

	fetchLocationFromGeo = geoId => (
		this.fetchLocationDataFromService(geoId)
			.then(entry => {
				if (typeof entry !== 'undefined') {
					return {
						latitude: entry.latitude,
						longitude: entry.longitude,
						city: entry.city_name,
						state: entry.subdivision_1_iso_code,
						country: entry.country_iso_code
					};
				}

				return null;
			})
	);

	fetchTimeSeries = (type, latitude, longitude, size='640x480') => (
		weatherClient.fetchTimeSeries(type, latitude, longitude, size)
	);

	fetchAlerts = ({ latitude, longitude, city, state, country }) => (
		weatherClient.fetchAlerts(latitude, longitude).then((data) => {
			if (data) {
				data.location = { city, state, country, latitude, longitude };
				return data;
			}
			console.error('No data given');
			return [];
		}).then(this._sanitizeAlerts).catch(e => null)
	);

	_fetchDetailedAlerts = key =>
		weatherClient.fetchAlertDetails(key).then(r => r.alerts_details || []);

	_sanitizeAlerts = data => {
		let alerts = data.alerts;
		if (!alerts || !alerts.length) return {};

		let description,
			color;

		// Set alerts based on order returned, with the first being
		// the highest severity and tally advisory types
		alerts.map((a, idx) => {
			if (alerts[idx].description) {
				// Build description or Alert + Advisory objects
				description = alerts[idx].description;
				color = alerts[idx].color;
			}
		});

		return {
			description,
			color,
			totalAlerts: alerts.length,
			alerts,
			location: data.location
		};
	};

	/**
	 *
	 * @param number zipcode - The the zipcode corresponding to a United States
	 * 							NOTE: Can we query weather endpoint with geoname or country code?
	 * @param number geoId - The unique geo ID value for the location to use instead of a zipcode if it
	 * 						is provided and {zipcode} isn't. This will cause an extra lookup to get
	 * 						the proper US zipcode to use with our current weather endpoints
	 *
	 * @returns Promise<Array<Object>>
	 */
	fetchAlertPage = (geoId) => (
		this.fetchLocationFromGeo(geoId)
			.then(this.fetchAlerts)
			.then(this._createAlertObjects)
			.catch(e => {
				console.error(e);
				return [];
			})
	)

	loadAlertsDetailsPage = (geoId) => (
		this.fetchAlertPage(geoId)
	)

	/**
	 * Takes an Array of properly formatted array objects and:
	 *  1: Creates a {{detailKey}} value that can be used to call the endpoint to get
	 *  	a detailed description of several alerts at once
	 *  2: Creates the {{alertObjects}} Array that has only the information we need
	 *  	from the individual alerts
	 *  3: Combines them in a single payload and returns it
	 *
	 *
	 * @param Object	data -
	 * @param Array<Object>	alerts -
	 * @param string		alerts[0].detail_key - Unique alert ID
	 * @param string		alerts[0].description - Short description used for the headline
	 * @param string		alerts[0].severity - 'minor'|'moderate'|'high'|'severe'
	 * @param string		alerts[0].source - Usually 'The Weather Channel'
	 * @param number		alerts[0].expire_time_gmt - Unix timestamp for alert expiration
	 * @returns Object|Throws
	 * @private
	 */
	_createAlertObjects = data => {
		let alerts = data.alerts,
			location = data.location;
		let [detailKey, alertObjects] = [alerts.map(a => a.detail_key).join(','), {}];
		if (!detailKey) {
			let noDetailKeyFoundError = new Error('No detail_key property was found');
			noDetailKeyFoundError.code = Errors.NO_DETAIL_KEY_FOUND;
			throw noDetailKeyFoundError;
		}

		for (let i = 0; i < alerts.length; i++) {
			alertObjects[alerts[i].detail_key] = {
				headlineText: alerts[i].description,
				color: alerts[i].color,
				source: alerts[i].source,
				expireTimeGmt: alerts[i].expiration,
				key: alerts[i].detail_key,
				detailedDesc: this._formatDetailedAlertDescription(alerts[i].detail_description)

			};
		}

		return { detailKey, alertObjects, location };
	};

	_formatDetailedAlertDescription(desc) {
		return (
			desc.replace(/&{2}/g, '').replace(/\n{4}/g, '').replace(/(^|[^\n])\n(?!\n)/g, '$1 ') || ''
		);
	}

	/**
	 * Fetch a forecast entry in hourly increments
	 *
	 * @param {Number} zipCode - The zipcode to pull forecast information for
	 * @returns {*}
	 */
	fetchHourlyForecast = weatherClient.fetchHourlyForecast;

	getDailyForecastData = (latitude, longitude) => (
		this.fetchHourlyForecast(latitude, longitude).then(data => (data || []).map(hourlyForecast => this._sanitizeHourlyForecast(hourlyForecast)))
	)

	/**
	 * Takes the payload from the weather service backend service and formats it
	 * nicely for use on the front end. This powers the <HourlyForecast /> component.
	 *
	 * @param {object} 		f
	 * @param {number} 		f.valid_until - The unix timestamp value for the forecast
	 * @param {number|null}	f.feels_like - The 'feels like' temperature value
	 * @param {string} 		f.icon_code - Powers our different icon mappings
	 * @param {string} 		f.phrase_22char - Short description of weather conditions
	 * @param {number|null}	f.pop - Point of precipitation
	 * @param {number|null}	f.rh - Relative humidity
	 * @param {number|null}	f.temp - Temperature
	 * @param {string}		f.wdir_cardinal - Cardinal wind-direciton
	 * @param {string|null}	f.wspd - Wind speed
	 *
	 * @returns {{
	 *		conditionDesc: string,
	 *		dayAndMonth: string,
	 *		feels: string,
	 *		formattedMobileTime: string,
	 *		formattedDesktopTime: string,
	 *		humidity: string,
	 *		iconCode: string,
	 *		precipitation: string,
	 *		temp: string,
	 *		tempCels: string,
	 *		timestamp: number,
	 *		windSpeed: string
	 *}}
	 * @private
	 */
	_sanitizeHourlyForecast = f => {
		let conditionDesc = f.description,
			precipitation = `${f.precip_prob || 0}%`,
			temp = `${f.imperial.temp || 0}°`,
			tempCels = `${Math.round(f.metric.temp) || 0}°`,
			timestamp = f.valid_until * 1000,
			windSpeed = this.createWindObject(f.wind_direction, Math.round(f.imperial.wind_speed)),
			feels = `${f.imperial.temp_feel || 0}°`,
			feelsCels = `${Math.round(f.metric.temp_feel) || 0}°`,
			humidity = `${f.humidity || 0}%`,
			iconCode = f.icon || '',
			{ dayAndMonth, formattedMobileTime, formattedDesktopTime } = this._formatTimestamp(timestamp);

		return {
			conditionDesc,
			dayAndMonth,
			feels,
			feelsCels,
			formattedDesktopTime,
			formattedMobileTime,
			humidity,
			iconCode,
			precipitation,
			temp,
			tempCels,
			timestamp,
			windSpeed
		};
	};

	_formatTimestamp = timestamp => {
		let start = new Date(timestamp);
		let dayAndMonth = `${start.getMonth() + 1}/${start.getDate()}`;
		let amPm = start.getHours() >= NOON ? 'PM' : 'AM';
		let formattedMobileTime = (start.getHours() % NOON || NOON) + amPm;
		let formattedDesktopTime = `${start.getHours() % NOON || NOON}${':00' + amPm}`;
		return { dayAndMonth, formattedMobileTime, formattedDesktopTime };
	};

	fetchCurrentConditions = (latitude, longitude, dayCount = DEFAULT_FORECAST_DAYS) =>
		Promise.all([
			weatherClient.fetchDailyForecast(latitude, longitude, dayCount),
			weatherClient.fetchCurrentConditions(latitude, longitude)
		]).then(([forecastData, current]) => {
			let forecast = forecastData.forecasts[0];
			return this._sanitizeCurrentConditions(forecast, current);
		});

	_sanitizeCurrentConditions = (forecast, current) => {
		let currentTemp = current.imperial.temp;
		let currentTempCels = Math.round(current.metric.temp);
		let conditions = replaceFwdSlash(current.description);
		let conditionDescription = current.description;
		let sunrise = forecast.sunrise;
		let sunset = forecast.sunset;
		let weatherIconCode = current.icon;
		let iconCodeNumeric = (current.icon) || '';

		let highTemp = forecast.imperial.temp_max || (forecast.night && forecast.night.imperial.temp_high) || 0;
		let highTempCels = Math.round(forecast.metric.temp_max) || (forecast.night && Math.round(forecast.night.metric.temp_high)) || 0;
		let lowTemp = forecast.imperial.temp_min || (forecast.night && forecast.night.imperial.temp_min) || 0;
		let lowTempCels = Math.round(forecast.metric.temp_min) || (forecast.night && Math.round(forecast.night.metric.temp_min)) || 0;

		let dayOrNight = Array.isArray(forecast.day) ? forecast.night : forecast.day;

		let precip = dayOrNight.precip_prob || 0;

		return {
			currentTemp,
			currentTempCels,
			conditions,
			conditionDescription,
			highTemp,
			highTempCels,
			lowTemp,
			lowTempCels,
			sunrise,
			sunset,
			weatherIconCode,
			iconCodeNumeric,
			precip
		};
	};

	fetchFeelsLike = (latitude, longitude) =>
		this.fetchHourlyForecast(latitude, longitude).then(d => this._sanitizeFeelsLike(d.slice(0, 1).pop()));

	_sanitizeFeelsLike = forecast => {
		let asOfTime = new Date();
		let feelsLike = forecast.imperial.temp_feel || 0;
		let feelsLikeCels = Math.round(forecast.metric.temp_feel) || 0;
		let uvIndex = forecast.uv_index || 0;
		let wind = this.createWindObject(forecast.wind_direction, Math.round(forecast.imperial.wind_speed));
		let humidity = forecast.humidity || 0;
		let dew = forecast.imperial.dew_point || 0;
		let dewCels = Math.round(forecast.metric.dew_point) || 0;
		let pressure = `${forecast.pressure || 0} in`;
		let visibility = `${forecast.imperial.vis} mi`;
		let pop = `${forecast.precip_prob || 0}%`;

		return {
			asOfTime,
			feelsLike,
			feelsLikeCels,
			uvIndex,
			wind,
			humidity,
			dew,
			dewCels,
			pressure,
			visibility,
			pop
		};
	};

	fetchLookAheadTiles = (latitude, longitude, dayCount = DEFAULT_FORECAST_DAYS, tileCount = 3) =>
		weatherClient
			.fetchDailyForecast(latitude, longitude, dayCount)
			.then(data => this._sanitizeLookingAhead(data, dayCount, tileCount));

	_sanitizeLookingAhead = (apiResponse, dayCount = 4, tileCount = 3) => {
		if (!apiResponse || !apiResponse.forecasts || !apiResponse.forecasts.length) {
			return [];
		}

		return apiResponse.forecasts
			.slice(0, dayCount)
			.map((f, i) => this._buildLookAheadTiles({ idx: i, ...f }))
			.reduce((a, b) => a.concat(b))
			.slice(0, tileCount);
	}

	_buildLookAheadTiles = forecast => {
		let tiles = [];

		// For the first entry/day only, we make two tiles out of one forecast
		if (forecast.idx === 0) {
			let date = new Date();

			// For some reason, if we're missing the day value, it's also an array because why not!
			// (It's probably something to do with Golang json parsing defaults)
			// If it is past 6pm, exclude day from the tiles, allowing us to show an extra day
			if (date.getHours() < TODAY_CUTTOFF && !Array.isArray(forecast.day) && Object.keys(forecast.day).length > 0) {
				tiles = tiles.concat(this._buildTile(forecast, true));
			}
			tiles = tiles.concat(this._buildTile(forecast));
		}
		else {
			// We'll always have a day object here, and should base most stuff off that
			tiles = tiles.concat(this._buildTile(forecast));
		}

		return tiles;
	};

	_buildTile = (forecast, isTodayTile) => {
		let [day, night] = [forecast.day, forecast.night];

		let isFirstTile = forecast.idx === 0;
		let isTonightTile = isFirstTile && !isTodayTile;

		// low and high now act as keys for il8n
		let highLowLabel = { low: isTonightTile, high: !isTonightTile };
		let highLowValue = isTonightTile ? forecast.imperial.temp_min : forecast.imperial.temp_max;
		let highLowValueCels = isTonightTile ? Math.round(forecast.metric.temp_min) : Math.round(forecast.metric.temp_max);

		let desc = isTonightTile ? night.phrase_long : day.phrase_long;
		let descCels = desc && this._addDegreeSymToString(this._convertDescToCelsius(desc));
		desc = desc && this._addDegreeSymToString(desc);
		let simpleDesc = isTonightTile ? night.phrase_short : day.phrase_short;

		let title;
		if (isFirstTile) {
			// Today and tonight now act as keys for il8n
			title = { today: isTodayTile, tonight: !isTodayTile };
		}
		else {
			title = { dow: forecast.day_of_week.toLowerCase() };
		}

		let precipChance = isTonightTile ? night.precip_prob || 0 : day.precip_prob || 0;

		let windSpeed = isTonightTile
			? this.createWindObject(night.wind_direction, Math.round(night.imperial.wind_speed))
			: this.createWindObject(day.wind_direction, Math.round(day.imperial.wind_speed));
		let uvIndex = isTonightTile ? `${night.uv_index || 0}` : `${day.uv_index || 0}`;

		let sunrise = handleSunFormat(forecast.sunrise) + this._getAmPm(forecast.sunrise);
		let sunset = handleSunFormat(forecast.sunset) + this._getAmPm(forecast.sunset);

		let humidity = isTonightTile ? night.humidity || 0 : day.humidity || 0;

		let dailyWeatherIconCode = forecast.day.icon || '';
		let nightlyWeatherIconCode = forecast.night.icon || '';
		let weatherIconCode = dailyWeatherIconCode || nightlyWeatherIconCode;

		return {
			highLowLabel,
			highLowValue,
			highLowValueCels,
			desc,
			descCels,
			simpleDesc,
			title,
			precipChance,
			windSpeed,
			uvIndex,
			sunrise,
			sunset,
			humidity,
			weatherIconCode
		};
	};

	_getAmPm = dateStr => {
		// Sample dateStr value: 2017-02-10T17:32:51-0800
		if (dateStr) {
			let a = dateStr.replace(/\D/g, ' ').split(' ');
			let amPm = ~~a[3] >= NOON ? 'pm' : 'am';
			return `${amPm}`;
		}
	};

	_formatLookAheadTimestamp = date => {
		let [hour, minutes] = [date.getHours() % NOON || NOON, date.getMinutes()];
		let amPm = date.getHours() >= NOON ? 'pm' : 'am';
		let minuteDisplay = minutes > 9 ? minutes : `${'0' + minutes}`;
		return `${hour}:${minuteDisplay} ${amPm}`;
	};

	/**
	 * Fetch a 5 day or 10 day forecast.
	 *
	 * Note: The Weather Channel API returns an extra entry, hence the slice(0, dayCount);
	 * @param {Number} zipCode - The zipcode to pull forecast information for
	 * @param {Number} dayCount - 5 or 10, the number of days to pull forecasts for
	 * @returns {Promise.resolve|Promise.reject}
	 */
	fetchDailyForecast = (latitude, longitude, dayCount = DEFAULT_FORECAST_DAYS) =>
		weatherClient
			.fetchDailyForecast(latitude, longitude, dayCount)
			.then(data => this._sanitizeDailyForecast(data, dayCount));


	_sanitizeDailyForecast = (apiResponse, dayCount = DEFAULT_FORECAST_DAYS) => {
		if (!apiResponse || !apiResponse.forecasts || !apiResponse.forecasts.length) {
			return [];
		}

		return apiResponse.forecasts.slice(0, dayCount).map((f, i) => this._buildForecast(f, i));
	}

	// apeltz: @FIXME: this formatting can probably be done with a more simple function... refactor needed
	_addDegreeSymToString = str => {
		if (!str.match(/\d(F|C)/g)) return str;
		str.match(/\d(F|C)/g).map(tempRaw => {
			let tempFormatted = ('' + tempRaw).split('').join('° ');
			str = str.replace(tempRaw, tempFormatted);
		});
		return str;
	};

	_convertDescToCelsius = desc => {
		let [regEx, formattedDesc] = [/\d{1,3}F/g, desc];

		(desc.match(regEx) || []).map(fTemp => {
			formattedDesc = formattedDesc.replace(
				fTemp,
				fahrenheitToCelsius(fTemp.slice(0, fTemp.length - 1)) + 'C'
			);
		});

		return formattedDesc;
	};

	_buildForecast = (forecast, idx) => {
		let validDate = new Date(Number(forecast.valid_until) * 1000);
		let hasDay = Object.keys(forecast.day).length > 0;

		//For first day, check to see if past cutoff to show day breakdown
		if (hasDay && idx === 0 && (new Date()).getHours() >= TODAY_CUTTOFF) {
			hasDay = false;
		}

		let dailyHigh = forecast.day.imperial.temp_high || 0;
		let dailyHighCels = Math.round(forecast.day.metric.temp_high) || 0;
		let nightlyHigh = forecast.night.imperial.temp_high || 0;
		let nightlyHighCels = Math.round(forecast.night.metric.temp_high) || 0;
		let maxTemp = hasDay ? forecast.imperial.temp_max || 0 : forecast.night.imperial.temp_high || 0;
		let maxTempCels = hasDay ? Math.round(forecast.metric.temp_max) || 0 : Math.round(forecast.night.metric.temp_high) || 0;
		let minTemp = forecast.imperial.temp_min || 0;
		let minTempCels = Math.round(forecast.metric.temp_min) || 0;

		let precipitationChance = (hasDay ? forecast.day.precip_prob || 0 : forecast.night.precip_prob || 0) + '%';
		let nightlyPrecipitation = (forecast.night.precip_prob || 0) + '%';
		let dailyPrecipitation = (forecast.day.precip_prob || 0) + '%';

		let dayAndMonthDisplay = `${validDate.getMonth() + 1}/${validDate.getDate()}`;
		let isForecastToday = isDateToday(validDate.getTime());


		let dowDisplay = forecast.day_of_week.toLowerCase();
		let dowDayDisplay;
		let dowNightDisplay;

		if (idx === 0) {
			dowDayDisplay = { today: hasDay, tonight: !hasDay };
			dowNightDisplay = { tonight: true };
		}
		else {
			dowDayDisplay = { dow: dowDisplay };
			dowNightDisplay = { dow: dowDisplay, isNight: true };
		}

		let dowDetailedNightDisplay = dowNightDisplay;

		let nightlyWindSpeed = this.createWindObject(forecast.night.wind_direction, Math.round(forecast.night.imperial.wind_speed));
		let dailyWindSpeed = this.createWindObject(forecast.day.wind_direction, Math.round(forecast.day.imperial.wind_speed));
		let windSpeed = hasDay ? dailyWindSpeed : nightlyWindSpeed;

		let dailyNarrative =
			forecast.day.narrative && this._addDegreeSymToString(forecast.day.narrative);
		let dailyNarrativeCels =
			forecast.day.narrative &&
			this._addDegreeSymToString(this._convertDescToCelsius(forecast.day.narrative));
		let nightlyNarrative =
			forecast.night.narrative && this._addDegreeSymToString(forecast.night.narrative);
		let nightlyNarrativeCels =
			forecast.night.narrative &&
			this._addDegreeSymToString(this._convertDescToCelsius(forecast.night.narrative));

		// apeltz: @NOTE: do NOT assign these to 'null' if absent from forecast... see https://github.com/facebook/react/issues/2380
		let dailyWeatherIconCode = forecast.day.icon || '';
		let nightlyWeatherIconCode = forecast.night.icon || '';
		let weatherIconCode = dailyWeatherIconCode || nightlyWeatherIconCode;

		let simpleDesc = hasDay ? forecast.day.phrase_short : forecast.night.phrase_short;
		let humidity = (hasDay ? forecast.day.humidity || 0 : forecast.night.humidity || 0) + '%';
		let uvIndex = hasDay ? forecast.day.uv_index || 0 : forecast.night.uv_index || 0;

		return {
			dailyHigh,
			dailyHighCels,
			dailyNarrative,
			dailyNarrativeCels,
			dailyPrecipitation,
			dailyWeatherIconCode,
			dailyWindSpeed,
			dayAndMonthDisplay,
			dowDayDisplay,
			dowDetailedNightDisplay,
			dowDisplay,
			dowNightDisplay,
			hasDay,
			humidity,
			idx,
			isForecastToday,
			maxTemp,
			maxTempCels,
			minTemp,
			minTempCels,
			nightlyHigh,
			nightlyHighCels,
			nightlyNarrative,
			nightlyNarrativeCels,
			nightlyPrecipitation,
			nightlyWeatherIconCode,
			nightlyWindSpeed,
			precipitationChance,
			simpleDesc,
			uvIndex,
			validDate,
			weatherIconCode,
			windSpeed
		};
	};

	createWindObject = (direction, speed) =>
		(speed ? { direction, speed: `${speed}mph` } : { calm: true });
}



// WEBPACK FOOTER //
// ../node_modules/@cube/weather-widget/src/model/index.js